import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const IncidentsContext = createContext(null);

// ─── Notification audio config ───────────────────────────────────────────────
// The backend picks WHICH tone + WHAT to say. Frontend just executes.
const NOTIFICATION_AUDIO_URLS = {
  fdny_full: "/sounds/fdny-mdt-ring.mp3",
  fdny_escalation: "/sounds/fdny-mdt-ring.mp3",
  nypd_1013: "/sounds/nypd-10-13.mp3",
};

const VOLUME_KEY = "psa.notif.volume"; // 0-100 (percent)
const AUDIO_ON_KEY = "psa.notif.on";   // "1" | "0"
const TTS_BRANCHES_KEY = "psa.tts.branches"; // JSON: {FIRE:true, POLICE:false, EMS:false, WEATHER:false}
const DEFAULT_TTS_BRANCHES = { FIRE: true, POLICE: false, EMS: false, WEATHER: false };

function readPersistedTtsBranches() {
  try {
    const raw = localStorage.getItem(TTS_BRANCHES_KEY);
    if (!raw) return { ...DEFAULT_TTS_BRANCHES };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_TTS_BRANCHES, ...(parsed && typeof parsed === "object" ? parsed : {}) };
  } catch {
    return { ...DEFAULT_TTS_BRANCHES };
  }
}

function readPersistedVolume() {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw == null) return 85;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return 85;
    return Math.max(0, Math.min(100, n));
  } catch {
    return 85;
  }
}

function readPersistedAudioOn() {
  try {
    const raw = localStorage.getItem(AUDIO_ON_KEY);
    if (raw == null) return true;
    return raw === "1";
  } catch {
    return true;
  }
}

// Generic WebAudio beep — used when the backend didn't attach a special notification.
function playAlertTone(priority = "medium", volumePct = 85) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    const freq = priority === "critical" ? 880 : priority === "high" ? 660 : 520;
    const vol = Math.max(0, Math.min(1, volumePct / 100));
    o.type = "square";
    o.frequency.setValueAtTime(freq, ctx.currentTime);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18 * vol + 0.0001, ctx.currentTime + 0.02);
    o.start();
    o.frequency.setValueAtTime(freq * 1.25, ctx.currentTime + 0.18);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    o.stop(ctx.currentTime + 0.65);
    o.onended = () => ctx.close();
  } catch {
    // ignore
  }
}

export function IncidentsProvider({ children }) {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const wsRef = useRef(null);

  // Audio state (persisted per-browser).
  const [audioOn, setAudioOnState] = useState(readPersistedAudioOn());
  const [volume, setVolumeState] = useState(readPersistedVolume()); // 0-100 percent
  const [ttsBranches, setTtsBranchesState] = useState(readPersistedTtsBranches());

  // Live refs so async WS handler always sees the latest values without
  // re-subscribing on every slider tick.
  const audioOnRef = useRef(audioOn);
  const volumeRef = useRef(volume);
  const ttsBranchesRef = useRef(ttsBranches);
  useEffect(() => { audioOnRef.current = audioOn; }, [audioOn]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { ttsBranchesRef.current = ttsBranches; }, [ttsBranches]);

  // Refs to the actual <audio> elements mounted at the bottom of the provider.
  // Using DOM <audio> (not in-memory `new Audio()`) is the reliable path for
  // Chrome/Safari/Firefox autoplay unlock after the user's first gesture.
  const audioRefs = useRef({
    fdny_full: null,
    fdny_escalation: null,
    nypd_1013: null,
  });

  const unlockedRef = useRef(false);

  // Unlock all mounted <audio> tags AND prime the speech synthesizer on the
  // first user gesture. Chrome/Safari grant "media playback allowed" to an
  // element (and to the SS engine) only if a play/speak call happens
  // synchronously inside a user gesture at least once.
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      unlockedRef.current = true;
      Object.values(audioRefs.current).forEach((el) => {
        if (!el) return;
        try {
          el.muted = true;
          const p = el.play();
          if (p && typeof p.then === "function") {
            p.then(() => {
              try { el.pause(); el.currentTime = 0; el.muted = false; } catch { /* noop */ }
            }).catch(() => {
              el.muted = false;
            });
          }
        } catch { /* noop */ }
      });
      // Prime SpeechSynthesis with an inaudible utterance so the very first
      // real announcement doesn't get swallowed by Chrome's cold-start behavior.
      try {
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
          const warm = new SpeechSynthesisUtterance(" ");
          warm.volume = 0;
          warm.rate = 1;
          window.speechSynthesis.speak(warm);
        }
      } catch { /* noop */ }
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
      document.removeEventListener("touchstart", unlock);
    };
    document.addEventListener("click", unlock);
    document.addEventListener("keydown", unlock);
    document.addEventListener("touchstart", unlock);
    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("keydown", unlock);
      document.removeEventListener("touchstart", unlock);
    };
  }, []);

  // ─── Serial notification queue ───────────────────────────────────────────
  // Every audible event (chime, MP3, TTS) is chained onto queueRef so two
  // incidents arriving simultaneously never talk over each other.
  const queueRef = useRef(Promise.resolve());

  const enqueue = useCallback((task) => {
    const next = queueRef.current.then(() => task()).catch((err) => {
      console.warn("[notification] queue task failed:", err && err.message);
    });
    queueRef.current = next;
    return next;
  }, []);

  // Play the mounted MP3 <audio> element; resolves on ended/error so the
  // queue can advance to TTS reliably.
  const playMp3Async = useCallback((key) => new Promise((resolve) => {
    const el = audioRefs.current[key];
    if (!el) return resolve();
    let done = false;
    const finish = () => { if (done) return; done = true; resolve(); };
    el.addEventListener("ended", finish, { once: true });
    el.addEventListener("error", finish, { once: true });
    try {
      el.pause();
      el.currentTime = 0;
      el.volume = Math.max(0, Math.min(1, volumeRef.current / 100));
      el.muted = false;
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.catch((err) => {
          console.warn("[notification] mp3 play() rejected:", err && err.message);
          finish();
        });
      }
    } catch (err) {
      console.warn("[notification] mp3 play threw:", err && err.message);
      finish();
    }
    // Failsafe — the FDNY MDT ring is ~1.8s; give it a wide ceiling.
    setTimeout(finish, 6000);
  }), []);

  // Short two-tone "new job" chime. Pitch scales with severity so dispatchers
  // hear urgency instantly (critical = sharp/high, low = deep/soft).
  // Base is A5 → D6; multiplier compounds on both tones.
  const playNewJobChime = useCallback((priority = "medium") => new Promise((resolve) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return resolve();
      const ctx = new Ctx();
      const vol = Math.max(0, Math.min(1, volumeRef.current / 100));
      const p = String(priority || "medium").toLowerCase();
      const pitchMul = p === "critical" ? 1.5 : p === "high" ? 1.2 : p === "low" ? 0.65 : 1.0;
      const beep = (t0, freq) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(freq, t0);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.35 * vol + 0.0001, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
        o.connect(g); g.connect(ctx.destination);
        o.start(t0); o.stop(t0 + 0.4);
        return o;
      };
      const now = ctx.currentTime;
      beep(now, 880 * pitchMul);              // A5-based
      const last = beep(now + 0.22, 1174.66 * pitchMul); // D6-based
      let done = false;
      const finish = () => {
        if (done) return; done = true;
        try { ctx.close(); } catch { /* noop */ }
        resolve();
      };
      last.onended = finish;
      setTimeout(finish, 900); // failsafe
    } catch {
      resolve();
    }
  }), []);

  // TTS that RESOLVES on end/error. No cancel() call — the queue already
  // guarantees serial playback, and calling cancel() would kill a prior
  // announcement mid-sentence.
  const speakAsync = useCallback((text) => new Promise((resolve) => {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) return resolve();
    try {
      // Some Chrome builds silently pause SS after long idle periods.
      // Always resume before speaking so incoming pages actually announce.
      try { window.speechSynthesis.resume(); } catch { /* noop */ }
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      u.pitch = 1.0;
      u.volume = Math.max(0, Math.min(1, volumeRef.current / 100));
      const voices = window.speechSynthesis.getVoices();
      const enVoice = voices.find((v) => v.lang && v.lang.startsWith("en"));
      if (enVoice) u.voice = enVoice;
      let done = false;
      const finish = () => { if (done) return; done = true; resolve(); };
      u.onend = finish;
      u.onerror = finish;
      window.speechSynthesis.speak(u);
      // Failsafe — some browsers silently drop `onend`. Cap at length·90ms+3s.
      setTimeout(finish, Math.max(3000, text.length * 90));
    } catch {
      resolve();
    }
  }), []);

  // Build the "New [Incident Type] at [Address]" line. City is included when
  // the street is missing so dispatchers still hear a location. Zip is always
  // excluded per the fire-notification spec.
  const buildDefaultTts = useCallback((incident) => {
    const callType = (incident && incident.call_type) || "incident";
    const street = (incident && incident.address) || "";
    const city = (incident && incident.city) || "";
    const location = street || city || "an unknown location";
    return `New ${callType} at ${location}`;
  }, []);

  // ─── Public play API used by WS handler ──────────────────────────────────
  const speak = useCallback((text) => {
    // Legacy sync speak — kept for anything outside the queue (e.g., manual
    // "test tone" that shouldn't wait behind pending announcements).
    if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      u.pitch = 1.0;
      u.volume = Math.max(0, Math.min(1, volumeRef.current / 100));
      const voices = window.speechSynthesis.getVoices();
      const enVoice = voices.find((v) => v.lang && v.lang.startsWith("en"));
      if (enVoice) u.voice = enVoice;
      window.speechSynthesis.speak(u);
    } catch { /* best-effort */ }
  }, []);

  // Queue a new-incident announcement: (custom MP3 OR chime) → TTS.
  // Runs strictly after any prior queued announcement finishes.
  const announceIncident = useCallback((incident, notification) => {
    enqueue(async () => {
      // 1) tone: prefer backend-attached notification MP3 (FDNY/NYPD), else
      //    fall back to the generic two-tone chime.
      if (notification && notification.audio && audioRefs.current[notification.audio]) {
        await playMp3Async(notification.audio);
      } else {
        await playNewJobChime(incident && incident.priority);
      }
      // 2) TTS: gated by per-branch settings (localStorage: `psa.tts.branches`).
      //    Defaults: Fire ON, everything else OFF. Weather is included for when
      //    weather alerts get wired in as incidents.
      const branchKey = String(incident && incident.branch || "").toUpperCase();
      const branchAllowsTts = !!ttsBranchesRef.current?.[branchKey];
      if (!branchAllowsTts) return;
      const line = (notification && notification.tts) || buildDefaultTts(incident);
      // Small gap so the chime tail doesn't blur into speech.
      await new Promise((r) => setTimeout(r, 150));
      await speakAsync(line);
    });
  }, [enqueue, playMp3Async, playNewJobChime, speakAsync, buildDefaultTts]);

  // Ref mirror so the WS effect never re-subscribes when the callback identity
  // changes — avoids "WebSocket closed before connection established" churn.
  const announceRef = useRef(announceIncident);
  useEffect(() => { announceRef.current = announceIncident; }, [announceIncident]);

  // De-dupe: React StrictMode's double-mount + the reconnecting-WS close-race
  // + occasional server rebroadcasts can deliver the same announcement twice.
  // We key on the SPOKEN CONTENT (incident.id + tts text) with a 6-second
  // window so any repeat of the same announcement — regardless of which WS
  // frame delivered it — is silently dropped. Fresh repages that carry
  // different text or hit outside the window still speak normally.
  const lastAnnouncedRef = useRef(new Map()); // incident.id -> {tts, at}
  const DEDUPE_WINDOW_MS = 6000;
  const shouldAnnounce = useCallback((type, incident, notification) => {
    if (!incident || !incident.id) return true;
    const tts = (notification && notification.tts) || `${type}:${incident.updated_at || ""}`;
    const key = `${incident.id}:${tts}`;
    const now = Date.now();
    const entry = lastAnnouncedRef.current.get(incident.id);
    if (entry && entry.key === key && (now - entry.at) < DEDUPE_WINDOW_MS) {
      return false;
    }
    lastAnnouncedRef.current.set(incident.id, { key, at: now });
    // GC old entries so the map doesn't grow forever.
    if (lastAnnouncedRef.current.size > 200) {
      for (const [k, v] of lastAnnouncedRef.current) {
        if (now - v.at > DEDUPE_WINDOW_MS * 4) lastAnnouncedRef.current.delete(k);
      }
    }
    return true;
  }, []);

  // ─── Incident data ───────────────────────────────────────────────────────
  const fetchIncidents = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/incidents", { params: { status_filter: "all", limit: 200 } });
      setIncidents(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchIncidents();
    const t = setInterval(fetchIncidents, 8000);
    return () => clearInterval(t);
  }, [user, fetchIncidents]);

  useEffect(() => {
    if (!user) return;
    // Reconnecting WebSocket with keepalive ping. The onmessage handler runs
    // through announceRef.current so we never rebuild this effect on volume
    // / audio-toggle changes — the socket stays sticky.
    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let attempt = 0;
    let cancelled = false;

    const clearTimers = () => {
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      // Exponential backoff capped at 30 s: 1s, 2s, 4s, 8s, 16s, 30s, 30s…
      const delay = Math.min(30_000, 1000 * Math.pow(2, attempt));
      attempt += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (cancelled) return;
      clearTimers();
      let wsUrl;
      try { wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws"; }
      catch { scheduleReconnect(); return; }
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attempt = 0; // reset backoff on any successful open
        // Resync in case we missed WS frames during the outage.
        fetchIncidents();
        // Keepalive: server responds to "ping" with "pong". 25 s is well under
        // the 60 s idle-timeout most reverse proxies default to.
        pingTimer = setInterval(() => {
          try {
            if (ws && ws.readyState === WebSocket.OPEN) ws.send("ping");
          } catch { /* noop */ }
        }, 25_000);
      };

      ws.onmessage = (ev) => {
        try {
          // Ignore keepalive echo so it doesn't hit JSON.parse.
          if (ev.data === "pong") return;
          const msg = JSON.parse(ev.data);
          const notif = msg.notification;
          if (msg.type === "new_incident") {
            setIncidents((prev) => [msg.incident, ...prev.filter((i) => i.id !== msg.incident.id)]);
            setActivePage({ ...msg.incident, kind: "new" });
            // Trigger the map auto-focus effect on subscribers (CAD + Active
            // Incidents pages both watch `lastUpdated`).
            setLastUpdated(msg.incident);
            // Chime + TTS "New <call_type> at <address>", or the FDNY/NYPD
            // custom MP3+TTS if the backend attached one. Serial queue keeps
            // two simultaneous incidents from talking over each other.
            if (audioOnRef.current && shouldAnnounce(msg.type, msg.incident, notif)) {
              announceRef.current(msg.incident, notif);
            }
          } else if (msg.type === "incident_updated") {
            setIncidents((prev) => prev.map((i) => i.id === msg.incident.id ? msg.incident : i));
            setLastUpdated(msg.incident);
            // Escalation notifications come through here (FDNY alarm ladder).
            // Non-notification updates stay silent so we don't chime for
            // every notes edit.
            if (audioOnRef.current && notif && shouldAnnounce(msg.type, msg.incident, notif)) {
              announceRef.current(msg.incident, notif);
            }
          } else if (msg.type === "page") {
            setActivePage({ ...msg.incident, kind: "repage" });
            setLastUpdated(msg.incident);
            if (audioOnRef.current && shouldAnnounce(msg.type, msg.incident, notif)) {
              announceRef.current(msg.incident, notif);
            }
            fetchIncidents();
          }
        } catch { /* malformed WS payload — ignore */ }
      };

      ws.onerror = () => { /* close will follow — handle reconnect there */ };

      ws.onclose = () => {
        clearTimers();
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      try { ws && ws.close(); } catch { /* already closed */ }
    };
  }, [user, fetchIncidents]);

  // ─── CRUD ────────────────────────────────────────────────────────────────
  const createIncident = async (payload) => {
    const { data } = await api.post("/incidents", payload);
    setLastUpdated(data);
    await fetchIncidents();
    return data;
  };
  const updateIncident = async (id, payload) => {
    const { data } = await api.patch(`/incidents/${id}`, payload);
    setLastUpdated(data);
    await fetchIncidents();
    return data;
  };
  const pageIncident = async (id) => {
    await api.post("/incidents/page", { incident_id: id });
  };
  const dismissPage = () => setActivePage(null);

  // Setters that also persist.
  const setAudioEnabled = useCallback((v) => {
    setAudioOnState(!!v);
    try { localStorage.setItem(AUDIO_ON_KEY, v ? "1" : "0"); } catch { /* noop */ }
  }, []);
  const setVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(100, Math.round(v)));
    setVolumeState(clamped);
    try { localStorage.setItem(VOLUME_KEY, String(clamped)); } catch { /* noop */ }
  }, []);
  const setTtsBranchEnabled = useCallback((branch, on) => {
    const key = String(branch || "").toUpperCase();
    setTtsBranchesState((prev) => {
      const next = { ...prev, [key]: !!on };
      try { localStorage.setItem(TTS_BRANCHES_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  // Play a short sample of the FDNY tone at the current volume — used by the
  // volume slider so users can dial in the level in their environment.
  const previewNotificationTone = useCallback(() => {
    const el = audioRefs.current.fdny_full;
    if (!el) {
      playAlertTone("high", volumeRef.current);
      return;
    }
    try {
      el.pause();
      el.currentTime = 0;
      el.volume = Math.max(0, Math.min(1, volumeRef.current / 100));
      el.muted = false;
      const p = el.play();
      if (p && typeof p.then === "function") p.catch(() => playAlertTone("high", volumeRef.current));
    } catch {
      playAlertTone("high", volumeRef.current);
    }
  }, []);

  return (
    <IncidentsContext.Provider
      value={{
        incidents, loading, fetchIncidents,
        createIncident, updateIncident, pageIncident,
        activePage, dismissPage,
        audioOn, setAudioEnabled,
        volume, setVolume,
        ttsBranches, setTtsBranchEnabled,
        previewNotificationTone,
        lastUpdated,
      }}
    >
      {/* Mounted <audio> tags — refs are captured so the WS handler plays them
          reliably even under strict autoplay policies. */}
      <div aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
        {Object.entries(NOTIFICATION_AUDIO_URLS).map(([key, src]) => (
          <audio
            key={key}
            ref={(el) => { audioRefs.current[key] = el; }}
            src={src}
            preload="auto"
            data-testid={`notification-audio-${key}`}
          />
        ))}
      </div>
      {children}
    </IncidentsContext.Provider>
  );
}

export const useIncidents = () => useContext(IncidentsContext);
